/**
 * Error Boundary component for catching and handling React component errors
 * Provides a user-friendly error display with retry functionality
 */

import React from 'react';
import { Container, Typography, Button, Alert, AlertTitle, Box } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // Enhanced error logging for debugging
    console.error('Error caught by ErrorBoundary:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      props: this.props
    });

    // Report to error monitoring service if available
    if (window.reportError) {
      window.reportError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
    
    // Call optional retry callback
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  render() {
    if (this.state.hasError) {
      const { fallback: Fallback, showDetails = false } = this.props;
      
      // Use custom fallback if provided
      if (Fallback) {
        return (
          <Fallback 
            error={this.state.error}
            onRetry={this.handleRetry}
            errorInfo={this.state.errorInfo}
          />
        );
      }

      // Default error UI
      return (
        <Container sx={{ textAlign: 'center', mt: 4, maxWidth: 'md' }}>
          <Box sx={{ mb: 3 }}>
            <ErrorOutlineIcon 
              sx={{ fontSize: 64, color: 'error.main', mb: 2 }} 
            />
            <Typography variant="h4" gutterBottom>
              Oops! Something went wrong
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              We encountered an unexpected error. Don't worry, your data is safe.
            </Typography>
          </Box>

          {showDetails && this.state.error && (
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              <AlertTitle>Error Details</AlertTitle>
              <Typography variant="body2" component="pre" sx={{ 
                fontSize: '0.85rem',
                overflow: 'auto',
                maxHeight: 200 
              }}>
                {this.state.error.message}
                {showDetails && this.state.error.stack && `\n\n${this.state.error.stack}`}
              </Typography>
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button 
              variant="contained" 
              startIcon={<RefreshIcon />}
              onClick={this.handleRetry}
              size="large"
            >
              Try Again
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => window.location.reload()}
            >
              Reload Page
            </Button>
          </Box>

          {process.env.NODE_ENV === 'development' && (
            <Box sx={{ mt: 3, textAlign: 'left' }}>
              <Typography variant="h6" gutterBottom>
                Development Info
              </Typography>
              <Alert severity="info">
                <pre style={{ fontSize: '0.75rem', margin: 0 }}>
                  {this.state.errorInfo?.componentStack}
                </pre>
              </Alert>
            </Box>
          )}
        </Container>
      );
    }

    return this.props.children;
  }
}

/**
 * ErrorDisplay component for consistent error messaging
 * 
 * @param {Error} error - Error object to display
 * @param {Function} onRetry - Callback for retry button
 * @param {string} context - Context description for the error
 * @param {string} severity - Severity level (error, warning, info)
 * @param {boolean} showRetry - Whether to show retry button
 */
export const ErrorDisplay = ({ 
  error, 
  onRetry, 
  context = 'operation',
  severity = 'error',
  showRetry = true 
}) => (
  <Container sx={{ textAlign: "center", mt: 4 }}>
    <Alert severity={severity} sx={{ mb: 2 }}>
      <AlertTitle>Unable to load {context}</AlertTitle>
      {error?.message || 'Please check your connection and try again.'}
      {error?.context && (
        <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
          Context: {error.context}
        </Typography>
      )}
    </Alert>
    {showRetry && onRetry && (
      <Button 
        variant="contained" 
        onClick={onRetry}
        startIcon={<RefreshIcon />}
      >
        Retry
      </Button>
    )}
  </Container>
);

export default ErrorBoundary;